import { Component, OnDestroy, OnInit, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgClass, NgIf } from '@angular/common';
import { SaveOrderDialog } from '../save-order-dialog/save-order-dialog';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { SpinnerOverlayService } from '../../Components/spinner/spinner-overlay.service';
import { MessageService } from '../../shared/message.service';
import { AccountService } from '../../login/account.service';
import { CartItem, ProductCartService } from '../product-cart-component/product-cart-service';
import { OrderService } from '../order-service';
import { Subject, takeUntil, tap } from 'rxjs';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ITEMS_PER_PAGE } from '../../core/config/pagination.constants';
import { ProductCartDeleteDialog } from '../product-cart-component/product-cart-delete-dialog/product-cart-delete-dialog';
import { ProductDetailDialog } from '../product-detail-dialog/product-detail-dialog';
import { IProduct } from '../product-catalog/product.model';
import { ConfirmOrderDialog } from '../confirm-order-dialog/confirm-order-dialog';
import { WarehouseService } from '../../shared/service/warehouse-service';
import { IWarehouse } from '../../shared/model/warehouse.model';


@Component({
  selector: 'app-confirm-order',
  imports: [
    TableModule,
    CommonModule,
    NgIf,
  ],
  templateUrl: './confirm-order.html',
  styleUrl: './confirm-order.css'
})

export class ConfirmOrder implements OnDestroy, OnInit {

  totalItems: number = 1;
  first = 0;
  rows = 6;
  page = 0;
  sortField = 'id';
  sortOrder = 0;
  lastEvent: any;
  cartItems: CartItem[] = [];
  private destroy$ = new Subject<void>();
  total = 0;
  lastSavedOrderNumber: string | null = null;
  activeWarehouse = signal<IWarehouse | null>(null);

  get leadTime(): number {
    return this.cartItems.reduce((max, item) => Math.max(max, Number(item.leadTime || 0)), 0);
  }

  constructor(
    private dialog: MatDialog,
    private spinner: SpinnerOverlayService,
    private messageService: MessageService,
    private accountService: AccountService,
    private orderService: OrderService,
    private productCartService: ProductCartService,
    private router: Router,
    private route: ActivatedRoute,
    private warehouseService: WarehouseService
  ) {
    effect(() => {
      const warehouseCode = this.accountService.selectedWarehouseCode();
      if (warehouseCode) {
        this.loadWarehouseInfo(warehouseCode);
      } else {
        this.activeWarehouse.set(null);
      }
    });
  }

  ngOnInit() {
    const currentOrderId = localStorage.getItem('orderId');
    if (currentOrderId) {
      this.handleLoading(true);
      this.orderService.findByParams({ id: currentOrderId }).pipe(takeUntil(this.destroy$)).subscribe({
        next: (res: any) => {
          this.handleLoading(false);
          const orders = res.body?.order?.order || [];
          if (orders.length > 0) {
            const currentStatus = orders[0].status;
            if (currentStatus === 'CONFIRMED') {
              this.cleanAndRedirectHome();
              return;
            }
          }
          this.initializeCart();
        },
        error: () => {
          this.handleLoading(false);
          this.initializeCart();
        }
      });
    } else {
      this.initializeCart();
    }
  }

  private cleanAndRedirectHome() {
    localStorage.removeItem('orderId');
    localStorage.removeItem('my_cart_items');
    localStorage.removeItem('orderItems');
    localStorage.removeItem('orderNumber');
    this.productCartService.clearCart();
    this.router.navigate(['/home/pedidos']);
  }

  private initializeCart() {
    const storedOrderItems = localStorage.getItem('my_cart_items') ?? localStorage.getItem('orderItems');
    if (storedOrderItems) {
      try {
        const parsed = JSON.parse(storedOrderItems);
        const rawItems = Array.isArray(parsed) ? parsed : (parsed.items ?? parsed);

        const customerAN = this.accountService.userIdentity()?.user.addressNumber;
        let itemIds: string[] = [];
        if (Array.isArray(rawItems)) {
          itemIds = rawItems.map((it: any) => it.id);
        }
        if (itemIds.length && customerAN) {
          this.productCartService.getPriceJDE(itemIds, customerAN.toString())
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (resp: any) => {
                const priceMap = new Map((resp?.priceItems ?? []).map((p: any) => [p.itemId, p.price]));
                if (Array.isArray(rawItems)) {
                  rawItems.forEach((it: any) => {
                    const price = priceMap.get(it.id);
                    it.price = typeof price === 'number' ? price : it.price;
                  });
                }

                if (Array.isArray(rawItems)) {
                  const mapped: CartItem[] = rawItems.map((it: any) => ({
                    id: it.id,
                    productId: Number(it.productId ?? it.product_id ?? 0),
                    productImage: it.productImage ?? it.product_image ?? '',
                    name: it.name ?? '',
                    description: it.description ?? '',
                    dimensions: it.dimensions ?? '',
                    thickness: it.thickness != null ? Number(it.thickness) : undefined,
                    color: it.color ? { id: Number(it.color.id), name: it.color.name, url: it.color.url ?? '' } : undefined,
                    texture: it.texture ?? '',
                    faceOne: it.faceOne ?? it.face_one ?? false,
                    faceTwo: it.faceTwo ?? it.face_two ?? false,
                    platformCount: it.platformCount != null ? Number(it.platformCount) : 0,
                    halfPlatformCount: it.halfPlatformCount != null ? Number(it.halfPlatformCount) : 0,
                    quarterPlatformCount: it.quarterPlatformCount != null ? Number(it.quarterPlatformCount) : 0,
                    eighthPlatformCount: it.eighthPlatformCount != null ? Number(it.eighthPlatformCount) : 0,
                    price: it.price != null ? Number(it.price) : 0,
                    leadTime: it.leadTime != null ? Number(it.leadTime) : 0,
                    quantity: it.quantity != null ? Number(it.quantity) : 0
                  }));
                  this.cartItems = mapped;
                  this.total = this.cartItems.reduce((s, it) => s + (it.price ?? 0) * (it.quantity ?? 0), 0);
                  this.totalItems = this.cartItems.length;
                  this.adjustPagination();
                  this.productCartService.clearCart();
                  mapped.forEach(mi => this.productCartService.addItem(mi));
                }
              }
            });
        }

        if (Array.isArray(rawItems)) {
          const mapped: CartItem[] = rawItems.map((it: any) => ({
            id: it.id,
            productId: Number(it.productId ?? it.product_id ?? 0),
            productImage: it.productImage ?? it.product_image ?? '',
            name: it.name ?? '',
            description: it.description ?? '',
            dimensions: it.dimensions ?? '',
            thickness: it.thickness != null ? Number(it.thickness) : undefined,
            color: it.color ? { id: Number(it.color.id), name: it.color.name, url: it.color.url ?? '' } : undefined,
            texture: it.texture ?? '',
            faceOne: it.faceOne ?? it.face_one ?? false,
            faceTwo: it.faceTwo ?? it.face_two ?? false,
            platformCount: it.platformCount != null ? Number(it.platformCount) : 0,
            halfPlatformCount: it.halfPlatformCount != null ? Number(it.halfPlatformCount) : 0,
            quarterPlatformCount: it.quarterPlatformCount != null ? Number(it.quarterPlatformCount) : 0,
            eighthPlatformCount: it.eighthPlatformCount != null ? Number(it.eighthPlatformCount) : 0,
            price: it.price != null ? Number(it.price) : 0,
            leadTime: it.leadTime != null ? Number(it.leadTime) : 0,
            quantity: it.quantity != null ? Number(it.quantity) : 0
          }));

          this.cartItems = mapped;
          this.total = this.cartItems.reduce((s, it) => s + (it.price ?? 0) * (it.quantity ?? 0), 0);
          this.totalItems = this.cartItems.length;
          this.adjustPagination();

          this.productCartService.clearCart();
          mapped.forEach(mi => this.productCartService.addItem(mi));
        }
      } catch (e) {
        console.error('Failed to parse stored order items', e);
      }
    }

    this.productCartService.cartItems$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.cartItems = items;
        this.total = this.cartItems.reduce((s, it) => s + it.price * it.quantity, 0);
        this.totalItems = this.cartItems.length;
        this.adjustPagination();
      });

    this.productCartService.refreshCartFromApi()
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  saveAsDraft() {
    const dialogRef = this.dialog.open(SaveOrderDialog, {
      panelClass: 'lexend-dialog',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.saveOrderAsDraft();
      }
    });
  }

  addMoreProducts() {
    this.router.navigate(['/home/pedidos/crear']);
  }

  confirmOrder() {
    const maxLeadTime = this.cartItems.reduce((max, item) => Math.max(max, Number(item.leadTime || 0)), 0);

    const dialogRef = this.dialog.open(ConfirmOrderDialog, {
      panelClass: 'lexend-dialog',
      disableClose: true,
      data: {
        mode: 'confirm',
        leadTime: maxLeadTime,
        onConfirm: () => {
          const body = this.buildSaveBody();
          if (!body) return null;

          const backupCart = [...this.cartItems];

          localStorage.removeItem('orderId');
          localStorage.removeItem('my_cart_items');
          localStorage.removeItem('orderItems');
          localStorage.removeItem('orderNumber');
          this.productCartService.clearCart();

          return this.orderService.saveAndInvoice(body).pipe(
            tap({
              error: () => {
                backupCart.forEach(item => this.productCartService.addItem(item));
              }
            })
          );
        }
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result === true) {
        this.router.navigate(['/home/pedidos']);
      }
    });
  }

  editItem(item: CartItem) {
    console.log(`Editando ítem: ${item.name} (ID: ${item.id})`);

  }

  handleLoading(loading: boolean) {
    if (loading) this.spinner.show(); else this.spinner.hide()
  }

  removeItemFromTheCart(itemId: string, itemName: string) {
    const dialogRef = this.dialog.open(ProductCartDeleteDialog, {
      data: {
        id: itemId,
        name: itemName
      },
      panelClass: 'lexend-dialog',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.productCartService.removeItem(itemId);
        // La suscripción actualizará cartItems; reforzamos paginación por seguridad
        this.adjustPagination();
      }
    });
  }

  getItemSize(item: any): string {
    const features: string[] = [];

    if (item.dimensions && item.dimensions !== 'NA') {
      features.push(`${item.dimensions} mm`);
    }

    if (item.thickness) {
      features.push(`${item.thickness} mm`);
    }

    return features.length > 0 ? features.join(' / ') : '';
  }

  getItemFeatures(item: any): string {
    const features: string[] = [];

    if (item.color?.name && item.color.name !== 'NA') {
      features.push(item.color.name);
    }

    if (item.texture && item.texture !== 'NA') {
      features.push(item.texture);
    }

    return features.length > 0 ? features.join(' / ') : '';
  }


  getItemCoating(item: CartItem): string {
    if (item.faceOne && !item.faceTwo) return '1 Cara';
    if (!item.faceOne && item.faceTwo) return '2 Caras';
    return 'Sin definir';
  }

  onPageChange(event: any) {
    this.first = event.first ?? this.first;
    this.rows = event.rows ?? this.rows;
  }

  private adjustPagination() {
    const total = this.cartItems?.length ?? 0;
    if (this.rows <= 0) this.rows = 6;
    // Calcular el primer índice válido para la página actual
    const maxFirst = total > 0 ? Math.floor((total - 1) / this.rows) * this.rows : 0;
    if (this.first > maxFirst) this.first = maxFirst;
    if (this.first < 0) this.first = 0;
  }

  private saveOrderAsDraft() {
    this.handleLoading(true);

    const body = this.buildSaveBody();
    if (!body) { this.handleLoading(false); return; }

    const backupCart = [...this.cartItems];

    localStorage.removeItem('orderId');
    localStorage.removeItem('my_cart_items');
    localStorage.removeItem('orderItems');
    localStorage.removeItem('orderNumber');
    this.productCartService.clearCart();

    this.orderService.saveAsDraft(body).subscribe({
      next: (response: any) => {
        this.handleLoading(false);
        this.handleSaveResponse(response);
        this.cleanAndRedirectHome();
        this.messageService.showSuccess('Pedido guardado como borrador');
      }, error: () => {
        this.handleLoading(false);
        backupCart.forEach(item => this.productCartService.addItem(item));
      }
    });
  }

  private saveThenOpenConfirmDialog() {
    this.handleLoading(true);

    const body = this.buildSaveBody();
    if (!body) { this.handleLoading(false); return; }

    this.orderService.saveAndInvoice(body).subscribe({
      next: (response: any) => {
        this.handleLoading(false);
        this.handleSaveResponse(response);

        this.messageService.showSuccess('Pedido guardado y facturado');
        localStorage.removeItem('orderId');
        localStorage.removeItem('my_cart_items');
        this.productCartService.cleanCartAfterSave();

        const maxLeadTime = this.cartItems.reduce((max, item) => Math.max(max, Number(item.leadTime || 0)), 0);

        const dialogRef = this.dialog.open(ConfirmOrderDialog, {
          panelClass: 'lexend-dialog',
          disableClose: true,
          data: { orderNumber: this.lastSavedOrderNumber, leadTime: maxLeadTime }
        });

        dialogRef.afterClosed().subscribe((result: any) => {
          if (result) {
            this.router.navigate(['/home/pedidos']);
          }
        });
      },
      error: () => {
        this.handleLoading(false);
      }
    });
  }

  private buildSaveBody(): any | null {
    const itemsCodeList = this.cartItems.map(item => ({
      id: item.id,
      quantity: item.quantity,
      platformCount: item.platformCount ?? 0,
      halfPlatformCount: item.halfPlatformCount ?? 0,
      quarterPlatformCount: item.quarterPlatformCount ?? 0,
      eighthPlatformCount: item.eighthPlatformCount ?? 0
    }));
    if (!itemsCodeList || itemsCodeList.length === 0) {
      this.messageService.showError("No hay ítems para guardar al pedido");
      return null;
    }

    const userID = this.accountService.userIdentity()?.user.id;
    const addressNumber = this.accountService.userIdentity()?.user.addressNumber;
    if (!userID || !addressNumber) {
      this.messageService.showError("No se pudo cargar la información del usuario. Por favor, recargue la página y vuelva a intentarlo");
      return null;
    }

    const body: any = { userID: userID!!, addressNumber: addressNumber!!, itemsCodeList: itemsCodeList!! };
    const storedOrderId = localStorage.getItem('orderId');
    const orderID = storedOrderId ? Number(storedOrderId) : null;
    if (orderID) body.orderID = orderID;

    return body;
  }

  private handleSaveResponse(response: any) {
    // Normalize response: some backends return { body: 'json-string' } (API Gateway style)
    let payload = response;
    if (response && response.body && typeof response.body === 'string') {
      try {
        payload = JSON.parse(response.body);
      } catch (e) {
        // ignore parse error, keep original response
      }
    } else if (response && response.body && typeof response.body === 'object') {
      payload = response.body;
    }

    // Manejo de mensaje de error corto proveniente del backend
    const errorMessage = payload?.errorMessage ?? payload?.message ?? null;
    if (errorMessage) {
      this.messageService.showError(errorMessage);
      return;
    }

    // Extract possible orderId and orderNumber from various response shapes
    const returnedOrderId = payload?.orderId ?? payload?.order?.order?.[0]?.id ?? payload?.order?.id ?? payload?.id ?? null;
    if (returnedOrderId) localStorage.setItem('orderId', String(returnedOrderId));

    const returnedOrderNumber = payload?.orderNumber
      ?? payload?.order?.order?.[0]?.orderNumber
      ?? payload?.order?.order?.[0]?.order_number
      ?? payload?.order?.orderNumber
      ?? payload?.order?.order_number
      ?? payload?.order?.order?.[0]?.order_number
      ?? payload?.order?.orderNumber
      ?? null;

    if (returnedOrderNumber) {
      this.lastSavedOrderNumber = returnedOrderNumber;
      localStorage.setItem('orderNumber', String(returnedOrderNumber));
    }
  }

  openProductDetailDialog(productOrPayload: IProduct | number | any) {
    let dataPayload: any = null;

    if (!productOrPayload) {
      this.messageService.showError('Producto inválido');
      return;
    }

    if (typeof productOrPayload === 'number') {
      dataPayload = { id: productOrPayload };
    }
    else if ((productOrPayload as IProduct).id && !(productOrPayload as any).preselected) {
      dataPayload = { id: (productOrPayload as IProduct).id };
    }
    else if ((productOrPayload as any).id) {
      dataPayload = productOrPayload;
    } else {
      this.messageService.showError("Error: El producto no cuenta con un identificador");
      return;
    }

    const dialogRef = this.dialog.open(ProductDetailDialog, {
      panelClass: 'lexend-dialog',
      data: dataPayload,
      width: '900px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      disableClose: true
    });

    const comp = dialogRef.componentInstance as ProductDetailDialog;
    if (comp?.saved) {
      comp.saved.pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
        if (!result) return;

        if (result.action !== 'save') return;

        if (result.cartItemId && result.updated) {
          this.productCartService.updateItemById(result.cartItemId, result.updated as Partial<CartItem>);
          return;
        }

      });
    }

  }

  openProductDetailByCartItem(item: CartItem) {
    const payload = {
      id: item.productId,
      cartItemId: item.id,
      readOnly: true,
      preselected: {
        dimensions: item.dimensions,
        thickness: item.thickness,
        color: item.color,
        texture: item.texture,
        description: item.description,
        faceOne: item.faceOne,
        faceTwo: item.faceTwo,
        quantity: item.quantity,
        platformCount: item.platformCount,
        halfPlatformCount: item.halfPlatformCount,
        quarterPlatformCount: item.quarterPlatformCount,
        eighthPlatformCount: item.eighthPlatformCount
      },
      editMode: true,
      resetSaved: true
    };

    console.log('payload', payload);
    this.openProductDetailDialog(payload);
  }

  private loadWarehouseInfo(code: string) {
    this.warehouseService.findByCode(code).subscribe({
      next: (res) => {
        const responseBody = res.body as any;
        const warehouse = responseBody?.warehouse as IWarehouse;
        this.activeWarehouse.set(warehouse || null);
      },
      error: () => {
        this.activeWarehouse.set(null);
      }
    });
  }

}
